import type { PageOpts } from './listingPaginationTypes'

const btnNav =
  'min-w-[32px] h-8 px-2 flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all'

/**
 * First / prev / page numbers / next / last — same pattern as InviteeList and Yardi invite list.
 */
export function ListingPagination({
  pageOptions,
  onPageChange,
  placement,
  dataTestIdPrefix = 'listing',
}: {
  pageOptions: PageOpts
  onPageChange: (p: number) => void
  /** `top`/`bottom` (Yardi) or `header`/`footer` (transfer / invitee-style) */
  placement: 'header' | 'footer' | 'top' | 'bottom'
  /** e.g. `yardi-invite` → `data-testid="yardi-invite-pagination-top"` */
  dataTestIdPrefix?: string
}) {
  const currentPage = pageOptions.current || 1
  const totalPages = pageOptions.last || 1

  if (totalPages < 1) return null

  return (
    <div
      data-testid={`${dataTestIdPrefix}-pagination-${placement}`}
      className="flex flex-wrap items-center justify-end gap-1.5"
    >
      <button
        type="button"
        className={btnNav}
        onClick={() => onPageChange(1)}
        disabled={currentPage === 1}
        title="First"
      >
        &laquo;
      </button>
      <button
        type="button"
        className={btnNav}
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        title="Previous"
      >
        &lsaquo;
      </button>
      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
        let pageNum: number
        if (totalPages <= 5) {
          pageNum = i + 1
        } else if (currentPage <= 3) {
          pageNum = i + 1
        } else if (currentPage >= totalPages - 2) {
          pageNum = totalPages - 4 + i
        } else {
          pageNum = currentPage - 2 + i
        }
        const isActive = currentPage === pageNum
        return (
          <button
            key={pageNum}
            type="button"
            className={`min-w-[32px] h-8 px-2 flex items-center justify-center rounded-lg text-sm font-medium transition-all border ${
              isActive
                ? 'border-button bg-button text-white shadow-md shadow-slate-300/30'
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:border-slate-300'
            }`}
            onClick={() => onPageChange(pageNum)}
          >
            {pageNum}
          </button>
        )
      })}
      <button
        type="button"
        className={btnNav}
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        title="Next"
      >
        &rsaquo;
      </button>
      <button
        type="button"
        className={btnNav}
        onClick={() => onPageChange(totalPages)}
        disabled={currentPage === totalPages}
        title="Last"
      >
        &raquo;
      </button>
    </div>
  )
}
